import React, { useState, useEffect } from 'react';
import { Settings, Lock, Eye, EyeOff, Globe, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = '/api';

// Comprehensive timezone list - US + International
const TIMEZONES = [
  // US Timezones
  { value: 'America/New_York', label: 'Eastern Time (ET) - New York', group: 'United States' },
  { value: 'America/Chicago', label: 'Central Time (CT) - Chicago', group: 'United States' },
  { value: 'America/Denver', label: 'Mountain Time (MT) - Denver', group: 'United States' },
  { value: 'America/Phoenix', label: 'Arizona Time (No DST) - Phoenix', group: 'United States' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT) - Los Angeles', group: 'United States' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT) - Anchorage', group: 'United States' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT) - Honolulu', group: 'United States' },
  // Canada
  { value: 'America/Toronto', label: 'Eastern Time - Toronto', group: 'Canada' },
  { value: 'America/Vancouver', label: 'Pacific Time - Vancouver', group: 'Canada' },
  { value: 'America/Edmonton', label: 'Mountain Time - Edmonton', group: 'Canada' },
  // Europe
  { value: 'Europe/London', label: 'GMT/BST - London', group: 'Europe' },
  { value: 'Europe/Paris', label: 'CET - Paris/Berlin', group: 'Europe' },
  { value: 'Europe/Amsterdam', label: 'CET - Amsterdam', group: 'Europe' },
  // Asia/Pacific
  { value: 'Asia/Dubai', label: 'Gulf Time - Dubai', group: 'Middle East' },
  { value: 'Asia/Kolkata', label: 'IST - India', group: 'Asia' },
  { value: 'Asia/Singapore', label: 'SGT - Singapore', group: 'Asia' },
  { value: 'Asia/Tokyo', label: 'JST - Tokyo', group: 'Asia' },
  { value: 'Asia/Shanghai', label: 'CST - China', group: 'Asia' },
  { value: 'Australia/Sydney', label: 'AEST - Sydney', group: 'Australia' },
  { value: 'Australia/Melbourne', label: 'AEST - Melbourne', group: 'Australia' },
  { value: 'Pacific/Auckland', label: 'NZST - Auckland', group: 'Pacific' },
  // Africa
  { value: 'Africa/Lagos', label: 'WAT - Lagos', group: 'Africa' },
  { value: 'Africa/Johannesburg', label: 'SAST - Johannesburg', group: 'Africa' },
  // South America
  { value: 'America/Sao_Paulo', label: 'BRT - Sao Paulo', group: 'South America' },
  { value: 'America/Mexico_City', label: 'CST - Mexico City', group: 'North America' },
  // UTC
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)', group: 'Universal' },
];

// Group timezones for the select dropdown
const TIMEZONE_GROUPS = [...new Set(TIMEZONES.map(tz => tz.group))];

const SettingsTab = () => {
  const [passwordForm, setPasswordForm] = useState({ current: '', new: '', confirm: '' });
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Timezone state
  const [selectedTimezone, setSelectedTimezone] = useState('America/New_York');
  const [loadingTimezone, setLoadingTimezone] = useState(true);
  const [savingTimezone, setSavingTimezone] = useState(false);

  // Fetch current timezone setting on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/content/settings`);
        if (response.data?.timezone) {
          setSelectedTimezone(response.data.timezone);
        }
      } catch (error) {
        console.error('Failed to fetch settings:', error);
      } finally {
        setLoadingTimezone(false);
      }
    };
    fetchSettings();
  }, []);

  // Save timezone
  const handleSaveTimezone = async () => {
    setSavingTimezone(true);
    const token = localStorage.getItem('admin_token');
    try {
      await axios.put(`${API}/content/settings`, {
        timezone: selectedTimezone
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Timezone updated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save timezone');
    } finally {
      setSavingTimezone(false);
    }
  };

  return (
    <div>
      <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
        <Settings className="w-5 h-5 text-[#FF4500]" />
        Account Settings
      </h3>
      
      {/* Timezone Configuration Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 max-w-lg mb-6">
        <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <Globe className="w-5 h-5 text-[#0055FF]" />
          Timezone Configuration
        </h4>
        <p className="text-sm text-gray-600 mb-4">
          Set the timezone for appointments and availability. All appointment times and scheduling will use this timezone.
        </p>
        
        {loadingTimezone ? (
          <div className="flex items-center gap-2 text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading timezone settings...
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#0A0A0A] mb-2">
                Select Timezone
              </label>
              <select
                value={selectedTimezone}
                onChange={(e) => setSelectedTimezone(e.target.value)}
                className="w-full h-12 px-4 bg-[#F4F4F5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0055FF] cursor-pointer"
                data-testid="timezone-select"
              >
                {TIMEZONE_GROUPS.map(group => (
                  <optgroup key={group} label={group}>
                    {TIMEZONES.filter(tz => tz.group === group).map(tz => (
                      <option key={tz.value} value={tz.value}>
                        {tz.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-700">
                <strong>Current Selection:</strong> {TIMEZONES.find(tz => tz.value === selectedTimezone)?.label || selectedTimezone}
              </p>
            </div>
            
            <button
              onClick={handleSaveTimezone}
              disabled={savingTimezone}
              className="w-full h-12 bg-[#0055FF] text-white rounded-lg hover:bg-[#0044CC] transition-all duration-300 font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="save-timezone-btn"
            >
              {savingTimezone ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Save Timezone
                </>
              )}
            </button>
          </div>
        )}
      </div>
      
      {/* Change Password Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 max-w-md">
        <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <Lock className="w-5 h-5 text-[#525252]" />
          Change Password
        </h4>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#0A0A0A] mb-2">
              Current Password
            </label>
            <div className="relative">
              <input
                type={showPasswords.current ? 'text' : 'password'}
                value={passwordForm.current}
                onChange={(e) => setPasswordForm({...passwordForm, current: e.target.value})}
                className="w-full h-12 px-4 pr-12 bg-[#F4F4F5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4500]"
                placeholder="Enter current password"
              />
              <button
                type="button"
                onClick={() => setShowPasswords({...showPasswords, current: !showPasswords.current})}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#525252] hover:text-[#0A0A0A]"
              >
                {showPasswords.current ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-[#0A0A0A] mb-2">
              New Password
            </label>
            <div className="relative">
              <input
                type={showPasswords.new ? 'text' : 'password'}
                value={passwordForm.new}
                onChange={(e) => setPasswordForm({...passwordForm, new: e.target.value})}
                className="w-full h-12 px-4 pr-12 bg-[#F4F4F5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4500]"
                placeholder="Enter new password (min 6 characters)"
              />
              <button
                type="button"
                onClick={() => setShowPasswords({...showPasswords, new: !showPasswords.new})}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#525252] hover:text-[#0A0A0A]"
              >
                {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-[#0A0A0A] mb-2">
              Confirm New Password
            </label>
            <div className="relative">
              <input
                type={showPasswords.confirm ? 'text' : 'password'}
                value={passwordForm.confirm}
                onChange={(e) => setPasswordForm({...passwordForm, confirm: e.target.value})}
                className="w-full h-12 px-4 pr-12 bg-[#F4F4F5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4500]"
                placeholder="Confirm new password"
              />
              <button
                type="button"
                onClick={() => setShowPasswords({...showPasswords, confirm: !showPasswords.confirm})}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#525252] hover:text-[#0A0A0A]"
              >
                {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
          
          <button
            onClick={async () => {
              if (!passwordForm.current || !passwordForm.new || !passwordForm.confirm) {
                toast.error('Please fill in all fields');
                return;
              }
              if (passwordForm.new !== passwordForm.confirm) {
                toast.error('New passwords do not match');
                return;
              }
              if (passwordForm.new.length < 6) {
                toast.error('New password must be at least 6 characters');
                return;
              }
              
              setChangingPassword(true);
              const token = localStorage.getItem('admin_token');
              try {
                await axios.post(`${API}/auth/change-password`, {
                  current_password: passwordForm.current,
                  new_password: passwordForm.new
                }, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Password changed successfully!');
                setPasswordForm({ current: '', new: '', confirm: '' });
              } catch (error) {
                toast.error(error.response?.data?.detail || 'Failed to change password');
              } finally {
                setChangingPassword(false);
              }
            }}
            disabled={changingPassword}
            className="w-full h-12 bg-[#FF4500] text-white rounded-lg hover:bg-[#E03E00] transition-all duration-300 font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {changingPassword ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Changing...
              </>
            ) : (
              <>
                <Lock className="w-4 h-4" />
                Change Password
              </>
            )}
          </button>
        </div>
      </div>

      {/* Clear All Data Section */}
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mt-6">
        <h4 className="font-semibold text-lg mb-2 text-red-700 flex items-center gap-2">
          Danger Zone
        </h4>
        <p className="text-sm text-red-600 mb-4">
          This will permanently delete all leads, appointments, and payments. This action cannot be undone.
        </p>
        <button
          onClick={async () => {
            if (!window.confirm('Are you sure you want to delete ALL data? This cannot be undone!')) {
              return;
            }
            const confirmText = window.prompt('Type DELETE to confirm:');
            if (confirmText !== 'DELETE') {
              toast.error('Confirmation text did not match. Data was NOT deleted.');
              return;
            }
            
            const token = localStorage.getItem('admin_token');
            try {
              const response = await axios.delete(`${API}/admin/clear-all-data`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              toast.success(`Data cleared: ${response.data.deleted.leads} leads, ${response.data.deleted.appointments} appointments, ${response.data.deleted.payments} payments`);
              window.location.reload();
            } catch (error) {
              toast.error(error.response?.data?.detail || 'Failed to clear data');
            }
          }}
          className="w-full h-12 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all duration-300 font-semibold flex items-center justify-center gap-2"
        >
          Clear All Data
        </button>
      </div>
    </div>
  );
};

export default SettingsTab;
